import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldCheck, KeyRound, Loader2, AlertCircle } from "lucide-react";
import { verify2FACode } from "../api/auth-api";
import { AppError } from "@/lib/api-client";
import { UserDto } from "@/types/auth";

interface TwoFactorDialogProps {
  open: boolean;
  tempToken: string;
  onSuccess: (data: { access_token: string; user: UserDto }) => void;
  onCancel: () => void;
}

export function TwoFactorDialog({
  open,
  tempToken,
  onSuccess,
  onCancel,
}: TwoFactorDialogProps) {
  const [code, setCode] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  // Auto-focus input and reset state when dialog opens
  React.useEffect(() => {
    if (open) {
      setCode("");
      setErrorMessage(null);
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const submitCode = React.useCallback(
    async (codeToVerify: string) => {
      const trimmed = codeToVerify.trim();
      if (!trimmed || isSubmitting) return;

      setIsSubmitting(true);
      setErrorMessage(null);

      try {
        const result = await verify2FACode({
          temp_token: tempToken,
          code: trimmed,
        });
        onSuccess(result);
      } catch (err: any) {
        if (err instanceof AppError) {
          setErrorMessage(err.errorDetails);
        } else {
          setErrorMessage("Verification failed. Please check your code.");
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [tempToken, isSubmitting, onSuccess]
  );

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawVal = e.target.value;
    // Strip extraneous whitespace
    const clean = rawVal.replace(/\s+/g, "");
    setCode(clean);

    // GitHub-style auto-submit when exactly 6 digits are typed or pasted
    const digitsOnly = clean.replace(/\D/g, "");
    if (digitsOnly.length === 6 && !isSubmitting) {
      submitCode(digitsOnly);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitCode(code);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent className="sm:max-w-[420px]" sheetOnMobile>
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-950 text-white dark:bg-zinc-100 dark:text-zinc-950 shadow-sm ring-1 ring-zinc-800/20 dark:ring-zinc-200/20">
            <ShieldCheck className="h-6 w-6 stroke-[2]" />
          </div>
          <DialogTitle className="text-center text-lg">
            Two-Factor Authentication
          </DialogTitle>
          <DialogDescription className="text-center">
            Enter the 6-digit code from your authenticator app, or use an 8-character recovery code.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {errorMessage && (
            <div className="flex items-center gap-2 rounded-md bg-danger-soft p-3 text-xs text-danger">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">
              Verification Code
            </label>
            <div className="relative">
              <Input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                autoFocus
                placeholder="000000"
                value={code}
                onChange={handleCodeChange}
                disabled={isSubmitting}
                className="text-center font-mono text-xl tracking-[0.25em] pl-10 h-12 rounded-xl"
                maxLength={10}
              />
              <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4.5 w-4.5 text-muted-foreground" />
            </div>
            <p className="text-[11px] text-muted-foreground text-center">
              Auto-submits instantly when 6 digits are entered
            </p>
          </div>

          <DialogFooter className="pt-2 flex flex-col-reverse sm:flex-row sm:justify-between gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !code.trim()}
              className="w-full sm:w-auto min-w-[120px] bg-zinc-950 text-white hover:bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 dark:hover:bg-white"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Verify Code"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
